import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Check } from '../check/Check'
import { SubmitCheckFormValues, SubmitCheckResult } from '../submitCheckModal/SubmitCheckModal.types'
import { SubmitCheckModal } from '../submitCheckModal/SubmitCheckModal'
import { useCreateSubmitCheckHelpers } from '../../hooks/useCreateSubmitCheckHelpers'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { useMutation, useQuery } from '@percent/utility'
import { PaymentDetailsCheckContainerProps } from './PaymentDetailsCheckContainer.types'
import {
  CheckType,
  MonetaryVerificationCheck
} from '@percent/admin-dashboard/api/actions/monetaryVerifications/monetaryVerifications.types'

export function PaymentDetailsCheckContainer({
  monetaryVerification,
  organisationName,
  partnerName,
  validationSubmission
}: Readonly<PaymentDetailsCheckContainerProps>) {
  const { monetaryVerificationsService } = useServices()
  const { t } = useTranslation()

  const [checkToSubmit, setCheckToSubmit] = useState<MonetaryVerificationCheck | null>(null)
  const [submitCheckStatus, setSubmitCheckStatus] = useState<SubmitCheckResult | null>(null)

  const { data, isLoading, errorMessage } = useQuery(
    ['v1/admin/disbursements/payment-details-checks', monetaryVerification.id],
    monetaryVerificationsService.getPaymentDetailsCheck,
    { verificationId: monetaryVerification.id },
    { enabled: !!monetaryVerification.id }
  )

  const { createRequestPayload, invalidateQueryKeys } = useCreateSubmitCheckHelpers(
    monetaryVerification,
    data?.status,
    validationSubmission
  )

  const [{ isPending }, { apiFunc: submitPaymentDetailsCheck }] = useMutation(
    monetaryVerificationsService.submitPaymentDetailsCheck,
    () => setSubmitCheckStatus(SubmitCheckResult.SUCCESS),
    () => setSubmitCheckStatus(SubmitCheckResult.ERROR)
  )

  const handleSubmit = (formData: SubmitCheckFormValues) => {
    submitPaymentDetailsCheck(createRequestPayload(formData))
  }

  const handleCloseModal = () => {
    setCheckToSubmit(null)
    setSubmitCheckStatus(null)

    if (submitCheckStatus === SubmitCheckResult.SUCCESS) {
      invalidateQueryKeys(['v1/admin/disbursements/payment-details-checks', monetaryVerification?.id])
    }
  }

  const checkTitle = t('typography.paymentDetailsCheck')

  return (
    <>
      <Check
        check={data ? { ...data, type: CheckType.PAYMENT_DETAILS_CHECK } : undefined}
        monetaryVerification={monetaryVerification}
        onCheckToSubmit={setCheckToSubmit}
        title={checkTitle}
        isLoading={isLoading}
        isError={!!errorMessage}
      />
      {checkToSubmit && data && (
        <SubmitCheckModal
          isOpen={!!checkToSubmit}
          onClose={handleCloseModal}
          partnerName={partnerName}
          organisationName={organisationName}
          onSubmit={handleSubmit}
          isPending={isPending}
          submitCheckStatus={submitCheckStatus}
          checkType={CheckType.PAYMENT_DETAILS_CHECK}
          checkStatus={data?.status}
          checkTitle={checkTitle}
        />
      )}
    </>
  )
}
